/**
 * ComparisonResultCard - Collapsible card for completed comparison analysis results
 *
 * Displays comparison results in the chat-style feed:
 * - Collapsed: Compact header with tickers, winner, confidence
 * - Expanded: Full ComparisonAnalysisPanel with all details
 */

import { Trophy, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ComparisonAnalysisPanel } from './ComparisonAnalysisPanel';
import { ExportMenu } from './ExportMenu';
import { handleFinancialExport } from '@/utils/exportUtils';
import type { ComparisonKillSheet, AnchoredSnapshot, FinancialKillSheet } from './FinancialAnalysisPanel';
import type { FinancialAgentProgress, MediatorCompilation } from './FinancialLogicTree';
import type { ExportFormat } from '@/types/researchModes';

interface ComparisonResultCardProps {
  comparisonKillSheet: ComparisonKillSheet;
  /** Mapped FinancialKillSheet for export compatibility */
  killSheet?: FinancialKillSheet;
  snapshotA?: AnchoredSnapshot;
  snapshotB?: AnchoredSnapshot;
  query: string;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  className?: string;
  bullAgents?: FinancialAgentProgress[];
  bearAgents?: FinancialAgentProgress[];
  mediatorCompilation?: MediatorCompilation;
}

export function ComparisonResultCard({
  comparisonKillSheet,
  killSheet,
  snapshotA,
  snapshotB,
  query,
  isCollapsed = false,
  onToggleCollapse,
  className,
  bullAgents,
  bearAgents,
  mediatorCompilation,
}: ComparisonResultCardProps) {
  const winner = comparisonKillSheet.recommended_ticker;
  const confidencePercent = Math.round(comparisonKillSheet.confidence * 100);

  const handleExport = async (format: ExportFormat) => {
    if (killSheet) {
      await handleFinancialExport(format, {
        killSheet,
        anchoredSnapshot: snapshotA,
        query,
        ticker: `${comparisonKillSheet.ticker_a} vs ${comparisonKillSheet.ticker_b}`,
        bullAgents: bullAgents || [],
        bearAgents: bearAgents || [],
        mediatorCompilation,
      });
    }
  };

  if (isCollapsed) {
    return (
      <div
        className={cn(
          'relative w-full bg-surface border border-amber-500/30 overflow-hidden rounded-xl transition-all duration-300',
          className
        )}
      >
        <div className="px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-amber-500/20">
                <Trophy className="w-4 h-4 text-amber-400" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-bold text-sm text-text-primary">
                    {comparisonKillSheet.ticker_a} vs {comparisonKillSheet.ticker_b}
                  </span>
                  <span className="text-xs font-bold uppercase text-amber-400">
                    {winner} WINS
                  </span>
                  <span className="px-1.5 py-0.5 rounded text-xs font-mono bg-amber-500/20 text-amber-400">
                    {confidencePercent}%
                  </span>
                </div>
                {comparisonKillSheet.comparison_reason && (
                  <p className="text-xs text-text-muted mt-0.5 truncate">
                    {comparisonKillSheet.comparison_reason}
                  </p>
                )}
              </div>
            </div>

            {onToggleCollapse && (
              <button
                onClick={onToggleCollapse}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-light rounded-lg transition-colors flex-shrink-0"
              >
                <ChevronDown className="w-4 h-4" />
                <span className="hidden sm:inline">Details</span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Expanded state
  return (
    <div
      className={cn(
        'relative w-full bg-surface border border-amber-500/30 overflow-hidden rounded-xl transition-all duration-300',
        className
      )}
    >
      {/* Header with collapse toggle */}
      <div className="px-4 py-3 border-b border-border-subtle bg-bar-bg/50">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-amber-500/20">
              <Trophy className="w-4 h-4 text-amber-400" />
            </div>
            <div className="flex items-center gap-2">
              <span className="font-mono font-bold text-sm text-text-primary">
                {comparisonKillSheet.ticker_a} vs {comparisonKillSheet.ticker_b}
              </span>
              <span className="font-mono text-xs text-text-muted uppercase tracking-wider">
                Financial Comparison
              </span>
              <span className="px-1.5 py-0.5 rounded text-xs font-mono bg-amber-500/20 text-amber-400">
                {confidencePercent}% confidence
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {killSheet && (
              <ExportMenu
                onExport={handleExport}
                reportTitle={`${comparisonKillSheet.ticker_a} vs ${comparisonKillSheet.ticker_b} Comparison`}
              />
            )}
            {onToggleCollapse && (
              <button
                onClick={onToggleCollapse}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-light rounded-lg transition-colors"
              >
                <ChevronUp className="w-4 h-4" />
                <span className="hidden sm:inline">Collapse</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Full comparison panel */}
      <div className="p-4">
        <ComparisonAnalysisPanel
          comparisonKillSheet={comparisonKillSheet}
          snapshotA={snapshotA}
          snapshotB={snapshotB}
        />
      </div>
    </div>
  );
}
